import { useWeb3React } from '@web3-react/core'
import { parseLocalActivity } from 'components/AccountDrawer/MiniPortfolio/Activity/parseLocal'
import { useAllTokensMultichain } from 'hooks/Tokens'
import useENSName from 'hooks/useENSName'
import { AlertTriangle, CheckCircle } from 'react-feather'
import { useTransaction } from 'state/transactions/hooks'
import { TransactionDetails } from 'state/transactions/types'
import styled, { css } from 'styled-components/macro'
import { EllipsisStyle, ThemedText } from 'theme'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'

const PopupRow = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 12px 40px 12px 14px;
  background: ${({ theme }) => theme.backgroundInteractive};
  border: none;
  text-align: left;
  cursor: pointer;
  border-radius: 12px;
`

const IconWrapper = styled.div<{ success: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  ${({ theme, success }) =>
    success
      ? css`
          background: ${theme.accentSuccess}22;
          color: ${theme.accentSuccess};
        `
      : css`
          background: ${theme.accentWarning}22;
          color: ${theme.accentWarning};
        `};
`

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`

const TitleText = styled(ThemedText.BodySmall)`
  font-weight: 600;
  font-size: 14px;
`

const Descriptor = styled(ThemedText.Caption)`
  ${EllipsisStyle}
  font-size: 13px;
`

function TransactionPopupContent({ tx, chainId }: { tx: TransactionDetails; chainId: number }) {
  const success = tx.receipt?.status === 1
  const tokens = useAllTokensMultichain()
  const activity = parseLocalActivity(tx, chainId, tokens)
  const { ENSName } = useENSName(activity?.otherAccount)

  if (!activity) return null

  const explorerUrl = getExplorerLink(chainId, tx.hash, ExplorerDataType.TRANSACTION)
  //console.log(activity.currencies)

  return (
    <PopupRow onClick={() => window.open(explorerUrl, '_blank')} data-testid="transaction-popup">
      <IconWrapper success={success}>{success ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}</IconWrapper>
      <ContentColumn>
        <TitleText>{activity.title}</TitleText>
        <Descriptor>
          {activity.descriptor}
          {ENSName ?? activity.otherAccount}
        </Descriptor>
      </ContentColumn>
    </PopupRow>
  )
}

export default function TransactionPopup({ hash }: { hash: string }) {
  const { chainId } = useWeb3React()

  const tx = useTransaction(hash)

  if (!chainId || !tx) return null

  return <TransactionPopupContent tx={tx} chainId={chainId} />
}
